"use client";

import { useState } from "react";
import { FaClock, FaExternalLinkAlt, FaMapMarkerAlt, FaPhone } from "react-icons/fa";

/* NAP + hours + Google Maps embeds, as supplied in the homepage content doc. */
const offices = [
  {
    tab: "Houston",
    name: "Ruff Roofing Houston",
    street: "11767 Katy Fwy, Suite 600",
    cityLine: "Houston, TX 77079, USA",
    phoneHref: "tel:+13463393405",
    phoneLabel: "+1 346-339-3405",
    hours: [
      { label: "Monday to Friday", value: "7:00 AM to 5:00 PM CST" },
      { label: "Emergency roofing assistance", value: "24/7" },
    ],
    mapsHref: "https://maps.app.goo.gl/M2yao4sV4cAsfAL2A",
    embed:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3462.7927718755845!2d-95.59364939999999!3d29.7836331!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640ddb1aca182f5%3A0xdadecce3d0e20a7d!2sRuff%20Roofing%20Houston!5e0!3m2!1sen!2s!4v1792143863262!5m2!1sen!2s",
  },
  {
    tab: "Dallas-Fort Worth",
    name: "Ruff Roofing Dallas-Fort Worth",
    street: "13500 Midway Rd, Suite 410",
    cityLine: "Farmers Branch, TX 75244, USA",
    phoneHref: "tel:+18443417663",
    phoneLabel: "+1 844-341-7663",
    hours: [{ label: "Monday to Sunday", value: "Open 24 hours" }],
    mapsHref: "https://maps.app.goo.gl/Y19qGvGtk1j5Gftv6",
    embed:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3348.6968337728754!2d-96.83793949999999!3d32.9326072!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c27b8de1bce39%3A0x3171c871c1cd6437!2sRuff%20Roofing%20Dallas-Fort%20Worth!5e0!3m2!1sen!2s!4v1792144120639!5m2!1sen!2s",
  },
  {
    tab: "Austin-San Antonio",
    name: "Ruff Roofing Austin-San Antonio",
    street: "1067 FM 306, Suite 104",
    cityLine: "New Braunfels, TX 78130, USA",
    phoneHref: "tel:+15122211858",
    phoneLabel: "+1 512-221-1858",
    hours: [{ label: "Monday to Sunday", value: "Open 24 hours" }],
    mapsHref: "https://maps.app.goo.gl/hg2yuHJwQLsKxKLE6",
    embed:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3464.2265642343564!2d-98.0963979!3d29.742155499999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xa04a875d5b66d05%3A0xc96bf16ec7586161!2sRuff%20Roofing%20Austin-San%20Antonio!5e0!3m2!1sen!2s!4v1792144167806!5m2!1sen!2s",
  },
];

export default function HomeLocations({
  only,
  heading = { lead: "Get in Touch With Ruff Roofing", accent: "at Your Nearest Location" },
}: {
  /** Show just one office (by tab name) with no tabs — used on city pages. */
  only?: "Houston" | "Dallas-Fort Worth" | "Austin-San Antonio";
  heading?: { lead: string; accent: string };
} = {}) {
  const [active, setActive] = useState(only ? offices.findIndex((o) => o.tab === only) : 0);
  const office = offices[active];

  return (
    <section className="bg-white px-6 py-14 md:py-20 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <h2 className="mx-auto max-w-4xl text-center font-['Outfit'] text-3xl font-extrabold leading-[1.1] text-[#1C1C1C] sm:text-4xl md:text-5xl">
          {heading.lead}{" "}
          <span className="text-[#DC3332]">{heading.accent}</span>
        </h2>

        {/* Location tabs */}
        {!only && (
        <div
          role="tablist"
          aria-label="Ruff Roofing locations"
          className="mx-auto mt-8 flex w-full max-w-2xl flex-col gap-1 rounded-2xl bg-[#DC3332] p-1.5 sm:flex-row sm:rounded-full"
        >
          {offices.map((o, i) => (
            <button
              key={o.tab}
              role="tab"
              type="button"
              aria-selected={i === active}
              aria-controls="home-location-panel"
              onClick={() => setActive(i)}
              data-ph-event="homepage_location_tab_clicked"
              data-ph-label={o.tab}
              data-ph-location="homepage_locations"
              className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors sm:rounded-full ${
                i === active ? "bg-white shadow" : "hover:bg-white/15"
              }`}
              // Inline color: a global button style overrides Tailwind text-* here
              // (same workaround as the ServiceAreaMap "Check" button).
              style={{ color: i === active ? "#1C1C1C" : "#ffffff" }}
            >
              {o.tab}
            </button>
          ))}
        </div>
        )}

        <div
          id="home-location-panel"
          role={only ? undefined : "tabpanel"}
          className="mt-10 grid gap-8 rounded-3xl bg-[#F3F3F3] p-6 ring-1 ring-black/5 md:p-10 lg:grid-cols-[1fr_1fr_1.3fr] lg:items-start"
        >
          <div>
            <h3 className="font-['Outfit'] text-2xl font-extrabold text-[#1C1C1C]">{office.name}</h3>
            <p className="mt-4 flex gap-3 text-[#1C1C1C]">
              <FaMapMarkerAlt className="mt-1 shrink-0 text-[#DC3332]" />
              <span>
                {office.street}
                <br />
                {office.cityLine}
              </span>
            </p>
            <a
              href={office.phoneHref}
              data-ph-location="homepage_locations"
              data-ph-label={office.phoneLabel}
              className="mt-3 flex items-center gap-3 font-semibold text-[#1C1C1C] hover:text-[#DC3332]"
            >
              <FaPhone className="shrink-0 text-[#DC3332]" />
              {office.phoneLabel}
            </a>
            <a
              href={office.mapsHref}
              target="_blank"
              rel="noopener noreferrer"
              data-ph-event="homepage_location_directions_clicked"
              data-ph-label={office.tab}
              data-ph-location="homepage_locations"
              className="mt-6 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#1C1C1C] px-6 text-sm font-bold text-white transition-colors hover:bg-[#DC3332]"
            >
              Get Directions
              <FaExternalLinkAlt className="text-[10px]" />
            </a>
          </div>

          <div>
            <h3 className="flex items-center gap-2 font-['Outfit'] text-2xl font-extrabold text-[#1C1C1C]">
              <FaClock className="text-lg text-[#DC3332]" />
              Business Hours
            </h3>
            <dl className="mt-4 space-y-3">
              {office.hours.map((h) => (
                <div key={h.label}>
                  <dt className="text-sm text-[#54595F]">{h.label}</dt>
                  <dd className="font-semibold text-[#1C1C1C]">{h.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="overflow-hidden rounded-2xl ring-1 ring-black/10">
            <iframe
              key={office.embed}
              src={office.embed}
              title={`Map of ${office.name}`}
              className="h-[280px] w-full border-0 md:h-[320px]"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </section>
  );
}
